import { useState } from 'react';
import { authClient } from '@/lib/authClient';
import { formatJstDateLabel, formatJstTime, type KioskData } from '@/lib/kiosk';
import ConfirmDialog from './ConfirmDialog';

// 端末の設定 (管理者メニューの「端末の設定」から開く別ページ)。
// 受付と関係ない状態表示とログアウトなどをここに分けている。
export default function KioskSettings({
  kiosk,
  done,
  total,
  onBack,
}: {
  kiosk: KioskData;
  done: number;
  total: number;
  onBack: () => void;
}) {
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloadedAt, setReloadedAt] = useState<Date | null>(null);
  const { data, today } = kiosk;

  const dayLabel =
    today.day === null
      ? `${formatJstDateLabel(today.jstDate)} ＝ 会期外（テスト表示・記録されません）`
      : `${formatJstDateLabel(today.jstDate)} ＝ ${today.day} 日目`;

  const rows: Array<[string, React.ReactNode]> = [
    ['ログイン中', data?.account.email ?? '—'],
    [
      '通信',
      kiosk.connection === 'online' ? (
        <b className="text-[#127a3e]">● 接続中</b>
      ) : (
        <b className="text-[#a15c00]">● 通信できません（受付は続けられます）</b>
      ),
    ],
    ['送信待ち', `${kiosk.queueLength} 件`],
    [
      '送れなかった記録',
      kiosk.rejectedRecords.length > 0 ? (
        <span className="text-[#b3261e]">
          {kiosk.rejectedRecords.length} 件（管理者メニューで確認）
          <button
            type="button"
            onClick={kiosk.clearRejected}
            className="ml-3 rounded-lg border-2 border-[#d9d9de] px-3 py-0.5 text-[16px] font-bold text-[#3a3a40]"
          >
            確認したので消す
          </button>
        </span>
      ) : (
        'なし'
      ),
    ],
    [
      '出展者一覧',
      `${data?.exhibitors.length ?? 0} 組 ／ ${kiosk.loadedAt ? `${formatJstTime(kiosk.loadedAt)} に読み込み` : '未読み込み'}（1 分ごとに自動で読み直し）`,
    ],
    ['受付日', dayLabel],
    ['本日の受付', `${done} / ${total}`],
  ];

  return (
    <div className="fixed inset-0 z-40 flex flex-col overflow-auto bg-[#f4f4f6]">
      <div className="flex items-center justify-between border-b border-[#d9d9de] bg-white px-6 py-4">
        <div className="text-[24px] font-extrabold">端末の設定</div>
        <button
          type="button"
          onClick={onBack}
          className="rounded-xl border-2 border-[#d9d9de] px-5 py-2 text-[20px] font-bold"
        >
          ‹ 戻る
        </button>
      </div>
      <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-3 p-4 sm:p-6">
        <div className="rounded-2xl bg-white px-6 py-4">
          <div className="mb-1 text-[16px] font-extrabold text-[#6b6b70]">この端末の状態</div>
          <table className="text-[19px]">
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k}>
                  <th className="whitespace-nowrap py-1 pr-5 text-left font-bold text-[#6b6b70]">{k}</th>
                  <td className="py-1">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <SettingRow
          title="出展者一覧を読み直す"
          description={
            reloadedAt
              ? `${formatJstTime(reloadedAt)} に読み直しました`
              : '配置を直した直後など、自動の読み直しを待たずに反映させたいときに使います'
          }
          action={reloading ? '読み直し中…' : '読み直す'}
          busy={reloading}
          onClick={async () => {
            setReloading(true);
            await Promise.all([kiosk.flush(), kiosk.reload()]);
            setReloading(false);
            setReloadedAt(new Date());
          }}
        />
        <SettingRow
          title="画面を再読み込み"
          description="表示がおかしいときに使います。送信待ちの記録は消えません"
          action="再読み込み"
          onClick={() => window.location.reload()}
        />
        <SettingRow
          title="ログアウト"
          description="当日の受付を終えるときに使います。次に使うときは再ログインが必要です"
          action="ログアウト"
          danger
          onClick={() => setConfirmLogout(true)}
        />
      </div>
      {confirmLogout && (
        <ConfirmDialog
          title="ログアウトしますか？"
          confirmLabel="ログアウト"
          tone="danger"
          onConfirm={async () => {
            await authClient.signOut();
            // 戻り先を付けない。同じ iPad で admin がログインしたときは管理画面が開き、
            // 受付端末のアカウントは / から受付画面に回される (RequireAdmin)
            window.location.href = '/sign-in';
          }}
          onCancel={() => setConfirmLogout(false)}
        >
          {kiosk.queueLength > 0 && (
            <div className="rounded-lg bg-[#fff4e0] px-4 py-2 text-[18px] text-[#a15c00]">
              送信待ちの記録が {kiosk.queueLength} 件あります。この端末に残り、同じアカウントでもう一度ログインしたときに送られます（別のアカウントでログインしても送られません）。
            </div>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}

function SettingRow({
  title,
  description,
  action,
  danger = false,
  busy = false,
  onClick,
}: {
  title: string;
  description: string;
  action: string;
  danger?: boolean;
  busy?: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-white px-6 py-4">
      <div>
        <div className="text-[22px] font-extrabold">{title}</div>
        <div className="mt-0.5 text-[16px] text-[#6b6b70]">{description}</div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onClick}
        className={`h-[60px] min-w-[180px] shrink-0 rounded-xl border-2 text-[20px] font-extrabold disabled:opacity-60 ${
          danger ? 'border-[#f0b8b3] text-[#b3261e]' : 'border-[#d9d9de] text-[#1d1d1f]'
        }`}
      >
        {action}
      </button>
    </div>
  );
}
